import React from 'react'
import {
  ChipProps,
  FilterChipProps,
  ChoiceChipProps,
  DropdownChipProps,
  InputChipProps
} from './Chip.type'
import { ChipVariant } from './ChipVariant.type'

const FilterChip = ({
  label,
  selected = false,
  disabled = false,
  className,
  testId,
  onToggle
}: FilterChipProps): JSX.Element => {
  const classNames = ['Chip', 'Chip--filter']
  if (className) classNames.push(className)

  const handleClick = () => {
    if (!disabled && onToggle) {
      onToggle(!selected)
    }
  }

  return (
    <button
      type="button"
      className={classNames.join(' ')}
      aria-pressed={selected}
      disabled={disabled}
      onClick={handleClick}
      data-testid={testId || null}
    >
      <span
        className={`Chip-icon${selected ? '' : ' is-filter-icon-hidden'}`}
        aria-hidden="true"
      >
        done
      </span>
      <span className="Chip-label">{label}</span>
    </button>
  )
}

const ChoiceChip = ({
  label,
  selected = false,
  disabled = false,
  value,
  tabIndex = -1,
  className,
  testId,
  onSelect
}: ChoiceChipProps): JSX.Element => {
  const classNames = ['Chip', 'Chip--choice']
  if (className) classNames.push(className)

  const handleClick = () => {
    if (!disabled && onSelect) {
      onSelect(value)
    }
  }

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={classNames.join(' ')}
      tabIndex={tabIndex}
      disabled={disabled}
      onClick={handleClick}
      data-testid={testId || null}
    >
      <span className="Chip-label">{label}</span>
    </button>
  )
}

const DropdownChip = ({
  label,
  expanded = false,
  disabled = false,
  controlsId,
  defaultLabel,
  leadingIcon,
  className,
  testId,
  onClick,
  children
}: DropdownChipProps): JSX.Element => {
  const classNames = ['Chip', 'Chip--dropdown']
  if (className) classNames.push(className)

  const containerClassNames = ['Chip-dropdownContainer']
  if (expanded) containerClassNames.push('is-open')

  return (
    <div className={containerClassNames.join(' ')}>
      <button
        type="button"
        className={classNames.join(' ')}
        data-chip-default-label={defaultLabel || label}
        aria-pressed={expanded}
        aria-expanded={expanded}
        aria-haspopup="menu"
        aria-controls={controlsId}
        disabled={disabled}
        onClick={onClick}
        data-testid={testId || null}
      >
        {leadingIcon && (
          <span className="Chip-icon" aria-hidden="true">
            {leadingIcon}
          </span>
        )}
        <span className="Chip-label" data-chip-dropdown-label="true">
          {label}
        </span>
        <span className="Chip-icon" aria-hidden="true">
          keyboard_arrow_down
        </span>
      </button>
      {children}
    </div>
  )
}

const InputChip = ({
  label,
  disabled = false,
  className,
  testId,
  onRemove
}: InputChipProps): JSX.Element => {
  const classNames = ['Chip', 'Chip--input']
  if (className) classNames.push(className)

  return (
    <button
      type="button"
      className={classNames.join(' ')}
      aria-label={`Remove ${label}`}
      disabled={disabled}
      onClick={onRemove}
      data-testid={testId || null}
    >
      <span className="Chip-label">{label}</span>
      <span className="Chip-icon" aria-hidden="true">
        close
      </span>
    </button>
  )
}

export const Chip = (props: ChipProps): JSX.Element => {
  switch (props.variant) {
    case ChipVariant.FILTER:
      return <FilterChip {...props} />
    case ChipVariant.CHOICE:
      return <ChoiceChip {...props} />
    case ChipVariant.DROPDOWN:
      return <DropdownChip {...props} />
    case ChipVariant.INPUT:
      return <InputChip {...props} />
  }
}
